import React from 'react';
import PropTypes from 'prop-types';

import env from 'Env';
import AddToCartBtn from 'Components/Cards/AddToCartBtn';
import BadgeSlash from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/BadgeSlash';
import LinkTag from 'Components/Carousel/LinkTag';
import PlatformIcons from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/PlatformIcons';
import PriceContainer from 'Components/Utility/Price/PriceContainer';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import QuickLook from 'Components/Cards/HitCardQuickLook';
import ReviewSection from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/ReviewSection';
import ScreenshotCarousel from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/ScreenshotCarousel';
import HitsCarousel from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/HitsCarousel';
import StarDealProgressBar from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/StarDealProgressBar';
import TagSection from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/TagSection';
import useViewport from 'Hooks/useViewport';
import WishlistButton from 'Components/Wishlist/WishlistButton';

export default function StarDealPanel({
  hit,
  siteTheme,
  slideClickFunction,
  slideExternalLink,
  slideUrl,
  switchCoverImg,
}) {
  const { isAboveMd, isAboveLg, isBelowXl } = useViewport();

  const scoreThreshold = env.percentRecommendedThreshold;

  const isBundleStarDeal = hit.type === 'bundle';
  const showReviews = (hit.rating_score
    || (hit.metacritic_score && hit.metacritic_score >= scoreThreshold)
    || (hit.opencritic_score && hit.opencritic_score >= scoreThreshold))
    && !hit.preorder; // will be used to evaluting showing user tags or review data

  let validDrm = '';
  let filteredDrm = [];
  if (hit && hit.drm) filteredDrm = hit.drm.filter(d => d !== 'voucher');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];

  const screenshots = hit.type === 'bundle' ? (hit.bundle_covers || []).map(h => h.cover) : hit.screenshots;

  const showAgeGate = hit.age_ratings && ProductRequirements.showAgeGate(hit.age_ratings);

  return (
    <div className="StarDealPanel CarouselPanel">
      <div className="CarouselPanel__topContainer">
        <div className="CarouselPanel__BadgeWishlistContainer">
          <BadgeSlash type="starDeal" />
          <WishlistButton
            noText={window.locale !== 'en' && isBelowXl}
            reducedText={window.locale !== 'en'}
            slug={hit.slug}
            productId={hit.product_id}
            product={hit}
          />
        </div>

        <h2 className="CarouselPanel__title">
          <LinkTag
            url={slideUrl}
            externalLink={slideExternalLink}
            className="faux-block-link__overlay-link"
            onClick={() => slideClickFunction()}
          >
            {hit.name}
          </LinkTag>
        </h2>


        {isAboveMd &&
          <React.Fragment>
            {showReviews ?
              <ReviewSection hit={hit} siteTheme={siteTheme} />
              :
              <TagSection tags={(hit.categories || []).slice(0, 4)} />
            }
          </React.Fragment>
        }

        {isAboveLg && !isBundleStarDeal &&
          <React.Fragment>
            {!showAgeGate &&
              <ScreenshotCarousel
                autoPlay
                type="product"
                screenshots={screenshots}
                switchCoverImg={switchCoverImg}
              />
            }
            {showAgeGate &&
              <div className="CarouselPanel__shortDesc CarouselPanel__section">
                {(hit.sdescs ? hit.sdescs[(window.locale || 'en')] : null) || hit.sdesc}
              </div>
            }
          </React.Fragment>
        }

        {isAboveLg && isBundleStarDeal &&
          <React.Fragment>
            <div className="CarouselPanel__quickLook">
              <QuickLook featuredCarousel hit={hit} />
            </div>

            <HitsCarousel
              autoPlay
              hits={hit.bundle_covers}
              switchCoverImg={switchCoverImg}
              type={hit.type}
            />
          </React.Fragment>
        }

        <StarDealProgressBar
          keysAvailablePercentage={hit.keysAvailablePercentage}
          starDealEndDate={hit.available_valid_until * 1000}
        />
      </div>

      <div className="CarouselPanel__bottomContainer">
        <PlatformIcons hit={hit} />
        <div className="CarouselPanel__bottomContainer__priceButton">
          {!hit.presale && !hit.free_to_play && !hit.hide_price &&
            <PriceContainer
              type={hit.type}
              discountPercent={hit.discount_percent}
              price={hit.price}
              tiered={hit.tiered}
              hideDiscount={hit.hide_discount}
              slug={hit.slug}
              discountPercentOverride={hit.discount_percent_override}
              fullPrice={hit.fullPrice}
              mystery={hit.mystery}
              drm={validDrm}
              payWhatYouWant={hit.pay_what_you_want}
            />
          }
          {!hit.tiered && !hit.presale && !hit.free_to_play &&
            <AddToCartBtn
              showInCartLink
              id={hit.product_id}
              slug={hit.slug}
              voucherToAdd={hit.addVoucher}
              hideButtonText={window.locale !== 'en' && isAboveMd}
            />
          }
        </div>
      </div>
    </div>
  );
}

StarDealPanel.propTypes = {
  hit: PropTypes.shape({}),
  siteTheme: PropTypes.string,
  slide: PropTypes.shape({}),
  slideClickFunction: PropTypes.func,
  slideExternalLink: PropTypes.bool,
  slideUrl: PropTypes.string,
  switchCoverImg: PropTypes.func,
};

StarDealPanel.defaultProps = {
  hit: {},
  siteTheme: '',
  slide: {},
  slideClickFunction: () => {},
  slideExternalLink: false,
  slideUrl: '',
  switchCoverImg: () => {},
};
